import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BillFile, BillStatus, ColumnKey, ParsedBillData, BillType, BillGroup, BillGroupMatchConfidence } from '../../models';
import { BillService } from '../../services/bill.service';
import { COLUMN_GROUPS } from '../../constants';
import { cleanIdentifierName, determineBillType, formatColumnLabel, formatColumnTooltip, genericUanLabel, resolveCanonicalLabel } from '../../utils/bill-utils';
import { formatBillDate } from '../../utils/date-format.util';
import { getScoreClass } from '../../utils/match-score.util';
import { SortableHeaderComponent } from '../shared/sortable-header.component';
import { ScoreSelectComponent, ScoreSelectOption } from '../bill-ai-flow/score-select/score-select.component';

/** Columns to hide in hierarchy view (already shown in the tree structure) */
const HIERARCHY_HIDDEN_COLUMNS: Set<string> = new Set([
    'legalEntity',
    // Account numbers are already rendered on the meter row itself (dropdown + inline secondary identifiers).
    'utilityAccountNumber',
    'utilityAccountNumber2',
]);
import { BillTypeBadgeComponent } from '../shared/bill-type-badge.component';
import { MatchStatusBadgeComponent } from '../shared/match-status-badge.component';

interface GroupedBill {
    period: string;
    bills: BillFile[];
    isDualBilling: boolean;
}

@Component({
  selector: 'app-hierarchy-meter-row',
  standalone: true,
  imports: [
      CommonModule,
      FormsModule,
      MatCheckboxModule,
      MatSelectModule,
      MatFormFieldModule,
      MatTooltipModule,
      SortableHeaderComponent,
      BillTypeBadgeComponent,
      MatchStatusBadgeComponent,
      ScoreSelectComponent
  ],
  templateUrl: './hierarchy-meter-row.component.html',
  styleUrl: './hierarchy-meter-row.component.scss'
})
export class HierarchyMeterRowComponent implements OnChanges {
    @Input() meter: any;
    @Input() visibleColumns: Record<ColumnKey, boolean> = {} as any;
    @Input() sortConfig: { key: ColumnKey; direction: 'asc' | 'desc' } | null = null;
    @Output() openPairReview = new EventEmitter<BillGroup>();

    get selectedBillIds(): string[] {
        return this.billService.selectedBillIds();
    }

    get isMeterSelected(): boolean {
        return this.billService.selectedMeterIds().includes(this.meter?.id);
    }

    toggleMeterSelection() {
        this.billService.toggleSelection(this.meter.id, 'meter');
    }

    expanded = false;
    isDragOver = false;
    groupedBills: GroupedBill[] = [];
    distinctIds: string[] = [];

    // Structured identifiers for drag/drop reassignment
    structuredIdentifiers: Array<{
        key: string;
        value: string;
        isPrimary: boolean;
        isSecondary: boolean;
        label: string;
    }> = [];

    // Deduplicated identifiers for the dropdown
    uniqueIdentifiers: Array<{ key: string; value: string; displayLabel: string }> = [];
    // Score-select options for the primary-account dropdown (shows badge +
    // confidence chip, mirroring the onboarding flow's meter-step dropdown).
    uanOptions: ScoreSelectOption[] = [];
    primaryAccountValue: string = '';
    secondaryLabels: string[] = [];

    // Pre-computed warning tooltip text per bill
    billWarningText: Record<string, string> = {};

    // Column header labels, pre-formatted once so the template can bind them
    // directly instead of calling formatColumnLabel on every CD cycle.
    columnLabels: Partial<Record<ColumnKey, string>> = {};
    // Optional long-form tooltip text per column (UAN 1 → "Utility Account
    // Number 1"). Empty for columns that don't need expansion.
    columnTooltips: Partial<Record<ColumnKey, string>> = {};

    // Centralized retriability signal from BillService (reactive, never stale)
    billRetriable = this.billService.billRetriable;

    // Constants exposed to template — filter out columns already shown in the hierarchy tree
    readonly COLUMN_GROUPS = COLUMN_GROUPS.map(group => ({
        ...group,
        columns: group.columns.filter(col => !HIERARCHY_HIDDEN_COLUMNS.has(col))
    }));
    readonly BillStatus = BillStatus;

    // Helper to check if a column is the first visible column in its group
    isFirstVisibleInGroup(groupIndex: number, colIndex: number, columns: ColumnKey[]): boolean {
        if (groupIndex === 0) return false; // First group never gets a section-start border
        // Find the first visible column index in this group
        for (let i = 0; i < columns.length; i++) {
            if (this.visibleColumns[columns[i]]) {
                return i === colIndex;
            }
        }
        return false;
    }

    // Helper to count visible columns in a group (for colspan)
    getVisibleColumnCount(columns: ColumnKey[]): number {
        return columns.filter(col => this.visibleColumns[col]).length;
    }

    // Getter for bill groups from meter
    get billGroups(): BillGroup[] {
        return this.meter?.billGroups || [];
    }

    // Check if there are any paired groups (utility + supplier)
    get hasPairedGroups(): boolean {
        return this.billGroups.some(g => g.utilityBill && g.supplierBill);
    }

    constructor(public billService: BillService) {
        this.precomputeColumnLabels();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['meter'] || changes['sortConfig']) {
            this.calculateGroupedBills();
            this.calculateDistinctIds();
            this.computeBillWarningText();
        }
    }

    /**
     * Column labels only depend on COLUMN_GROUPS (a module-level constant),
     * so we compute them once per component instance and let the template
     * bind `columnLabels[key]` instead of calling formatColumnLabel on every
     * change-detection cycle.
     */
    private precomputeColumnLabels(): void {
        for (const group of this.COLUMN_GROUPS) {
            for (const key of group.columns) {
                this.columnLabels[key] = formatColumnLabel(key);
                const tooltip = formatColumnTooltip(key);
                if (tooltip) this.columnTooltips[key] = tooltip;
            }
        }
    }

    private computeBillWarningText(): void {
        const text: Record<string, string> = {};
        const bills: BillFile[] = this.meter?.bills || [];
        for (const bill of bills) {
            if (bill.warnings?.length) {
                text[bill.id] = bill.warnings.join('\n');
            }
        }
        this.billWarningText = text;
    }

    getBillType(data: ParsedBillData | null): BillType {
        return determineBillType(data);
    }

    getUtilityDisplay(data: ParsedBillData | null): string {
        if (!data) return '-';
        if (data.utilityName) return data.utilityName;
        if (data.billType === 'supplier') return 'Supplier Bill Only';
        return '-';
    }

    // Helper to safely get data for display in the template
    getBillDataValue(bill: BillFile, key: string): any {
        if (!bill.data) return '-';

        switch (key) {
            case 'startDate': return bill.data.billingPeriodStart ? formatBillDate(bill.data.billingPeriodStart) : '-';
            case 'endDate': return bill.data.billingPeriodEnd ? formatBillDate(bill.data.billingPeriodEnd) : '-';
            case 'usage': return bill.data.usageAmount ? `${bill.data.usageAmount.toLocaleString()} ${bill.data.usageUnit || ''}` : '-';
            case 'amount': return bill.data.totalAmount ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.totalAmount)) : '-';
            case 'utilityAccountNumber': return bill.data.utilityAccountNumber || '-';
            case 'utilityAccountNumber2': return bill.data.utilityAccountNumber2 || '-';
            case 'confidence': return bill.data.confidence ? `${Math.round(bill.data.confidence * 100)}%` : '-';
            case 'supplyCharges': return bill.data.supplyCharges ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.supplyCharges)) : '-';
            case 'supplyTaxes': return bill.data.supplyTaxes ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.supplyTaxes)) : '-';
            case 'distributionCharges': return bill.data.distributionCharges ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.distributionCharges)) : '-';
            case 'distributionTaxes': {
                const taxes = bill.data.distributionTaxes;
                if (typeof taxes === 'number') {
                    return taxes ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(taxes)) : '-';
                }
                return '-';
            }
            // Pro mode charge groups
            case 'supplyContractCharges':
            case 'supplyEnergyCharges':
            case 'supplyCapacityCharges':
            case 'supplyTransmissionCharges':
            case 'supplyMarketCharges':
            case 'supplyLineLossCharges':
            case 'supplyGrtSut':
            case 'supplyMiscCharges':
            case 'supplySalesTaxes':
            case 'distributionDemandCharges':
            case 'distributionEnergyCharges':
            case 'distributionCustomerCharges':
            case 'distributionMiscCharges':
            case 'distributionTaxCharges': {
                const chargeGroup = (bill.data as any)[key];
                if (chargeGroup && typeof chargeGroup === 'object' && 'total' in chargeGroup) {
                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(chargeGroup.total);
                }
                return '-';
            }
            default: return (bill.data as any)[key] || '-';
        }
    }

    onToggleSelection(id: string) {
        this.billService.toggleSelection(id, 'bill');
    }
    
    calculateDistinctIds() {
        const ids = new Set<string>();
        const identifiersMap = new Map<string, { key: string; value: string; isPrimary: boolean; isSecondary: boolean }>();

        // Canonical label from the utility_account_number table wins over the
        // AI-extracted verbatim label when the backend produced one — falls
        // back to additionalIdentifiers so older bills still render correctly.
        const findLabel = (b: BillFile, value: string): string | null => {
            const canonical = resolveCanonicalLabel(b.data, value);
            if (canonical) return cleanIdentifierName(canonical);
            if (b.data?.additionalIdentifiers) {
                const identifier = b.data.additionalIdentifiers.find(id => id.value === value);
                if (identifier) return cleanIdentifierName(identifier.name);
            }
            return null;
        };

        // Highest confidence (0-100 int) seen for each identifier value, so
        // multi-bill meters show the best extraction score rather than the first.
        const confidenceByValue = new Map<string, number>();
        const recordConfidence = (value: string, confidence: number | undefined): void => {
            if (confidence == null) return;
            const pct = Math.round(confidence * 100);
            const existing = confidenceByValue.get(value);
            if (existing == null || pct > existing) {
                confidenceByValue.set(value, pct);
            }
        };

        this.meter.bills.forEach((b: BillFile) => {
            if (b.data?.utilityAccountNumber) {
                const val = b.data.utilityAccountNumber;
                const label = findLabel(b, val) || genericUanLabel(1);
                ids.add(`${label}: ${val}`);
                if (!identifiersMap.has(val)) {
                    identifiersMap.set(val, { key: label, value: val, isPrimary: true, isSecondary: false });
                }
            }
            if (b.data?.utilityAccountNumber2) {
                const val = b.data.utilityAccountNumber2;
                const label = findLabel(b, val) || genericUanLabel(2);
                ids.add(`${label}: ${val}`);
                if (!identifiersMap.has(val)) {
                    identifiersMap.set(val, { key: label, value: val, isPrimary: false, isSecondary: true });
                }
            }
            if (b.data?.additionalIdentifiers) {
                b.data.additionalIdentifiers.forEach((identifier) => {
                    const name = cleanIdentifierName(identifier.name);
                    ids.add(`${name}: ${identifier.value}`);
                    if (!identifiersMap.has(identifier.value)) {
                        identifiersMap.set(identifier.value, { key: name, value: identifier.value, isPrimary: false, isSecondary: false });
                    }
                    recordConfidence(identifier.value, identifier.confidence);
                });
            }
        });

        this.distinctIds = Array.from(ids);

        // Build structured identifiers with labels
        this.structuredIdentifiers = Array.from(identifiersMap.values()).map(id => ({
            ...id,
            label: `${id.key}: ${id.value}`
        }));

        // Deduplicate by normalized value (strip dashes, spaces, dots)
        const normalizedGroups = new Map<string, typeof this.structuredIdentifiers>();
        for (const id of this.structuredIdentifiers) {
            const norm = this.normalizeId(id.value);
            if (!normalizedGroups.has(norm)) {
                normalizedGroups.set(norm, []);
            }
            normalizedGroups.get(norm)!.push(id);
        }

        this.uniqueIdentifiers = [];
        for (const [, group] of normalizedGroups) {
            // Prefer the primary version, then the longest (most formatted) version
            const primary = group.find(g => g.isPrimary);
            const best = primary || group.sort((a, b) => b.value.length - a.value.length)[0];
            this.uniqueIdentifiers.push({
                key: best.key,
                value: best.value,
                // Include the identifier name so users can tell ESIID apart from
                // Account Number when a utility exposes more than one.
                displayLabel: `${best.key}: ${best.value}`
            });
        }

        // Set the primary account value for the dropdown
        const primaryId = this.uniqueIdentifiers.find(id =>
            this.structuredIdentifiers.some(si => si.value === id.value && si.isPrimary)
        );
        this.primaryAccountValue = primaryId?.value || this.uniqueIdentifiers[0]?.value || '';

        // Secondary labels: unique IDs that aren't the current primary
        this.secondaryLabels = this.uniqueIdentifiers
            .filter(id => id.value !== this.primaryAccountValue)
            .map(id => `${id.key}: ${id.value}`);

        // Score-select options: render the value as the main label, the
        // identifier name as a badge (ESIID, Account Number), and the
        // confidence score as a colored chip — matches the onboarding flow.
        this.uanOptions = this.uniqueIdentifiers.map(id => {
            const pct = confidenceByValue.get(id.value);
            return {
                value: id.value,
                label: id.value,
                badge: id.key,
                score: pct,
                scoreClass: pct != null ? getScoreClass(pct) : undefined,
            };
        });
    }

    private normalizeId(value: string): string {
        return value.replace(/[-\s_.]/g, '').toLowerCase();
    }

    onPrimaryAccountChange(value: string): void {
        const identifier = this.structuredIdentifiers.find(id => id.value === value);
        if (identifier && !identifier.isPrimary) {
            this.promoteIdentifierToPrimary(identifier);
        }
    }

    calculateGroupedBills() {
        // 1. Base Sort (Date Descending)
        const sorted = [...this.meter.bills].sort((a: BillFile, b: BillFile) => {
            const dateA = a.data?.billingPeriodStart || '0000-00-00';
            const dateB = b.data?.billingPeriodStart || '0000-00-00';
            return dateB.localeCompare(dateA);
        });

        const groups: GroupedBill[] = [];
        const processedIds = new Set<string>();

        sorted.forEach((bill) => {
            if (processedIds.has(bill.id)) return;

            const startDate = new Date(bill.data?.billingPeriodStart || '2000-01-01');
            const usage = bill.data?.usageAmount || 0;
            
            // Dual Billing Logic
            const pair = sorted.find(b => {
                if (b.id === bill.id || processedIds.has(b.id)) return false;
                
                const bDate = new Date(b.data?.billingPeriodStart || '2000-01-01');
                const diffTime = Math.abs(startDate.getTime() - bDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
                
                const bUsage = b.data?.usageAmount || 0;
                const usageDiff = Math.abs(usage - bUsage);
                
                const isUsageMatch = usage > 0 ? (usageDiff / usage) < 0.05 : usageDiff < 5;
                
                return diffDays <= 5 && isUsageMatch;
            });

            const currentGroupBills = [bill];
            if (pair) {
                currentGroupBills.push(pair);
                processedIds.add(pair.id);
            }
            processedIds.add(bill.id);

            const dateStr = bill.data?.billingPeriodStart || 'Undated';
            const periodLabel = dateStr.length >= 7 ? dateStr.substring(0, 7) : 'Undated';

            const hasUtility = currentGroupBills.some(b => this.getBillType(b.data) === 'utility');
            const hasSupplier = currentGroupBills.some(b => this.getBillType(b.data) === 'supplier');

            const isDual = currentGroupBills.length > 1 && hasUtility && hasSupplier;

            groups.push({
                period: periodLabel,
                bills: currentGroupBills.sort((a,b) => this.getBillType(a.data) === 'utility' ? -1 : 1),
                isDualBilling: isDual
            });
        });

        // 2. Apply User Sort to Groups
        if (this.sortConfig) {
            groups.sort((groupA, groupB) => {
                // Simplified sort for now
                return 0;
            });
        }
        this.groupedBills = groups;
    }

    // Handlers
    onDragOver(e: DragEvent) {
        e.preventDefault();
        if (e.dataTransfer?.types.includes('bill-id')) {
            this.isDragOver = true;
        }
    }

    onDragLeave(e: DragEvent) {
        this.isDragOver = false;
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        this.isDragOver = false;

        // Handle bill being dropped onto this meter
        const billId = e.dataTransfer?.getData('bill-id');
        if (billId) {
            this.billService.moveBillToMeter(billId, this.meter.id, this.meter.accountNumber);
        }
    }

    onBillDragStart(e: DragEvent, billId: string) {
        e.dataTransfer?.setData('bill-id', billId);
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
    }

    onMeterDragStart(e: DragEvent) {
        // Include meter ID and all bill IDs so the receiving component can move all of them
        const billIds = this.meter.bills.map((b: BillFile) => b.id);
        const meterData = JSON.stringify({ meterId: this.meter.id, billIds });
        e.dataTransfer?.setData('meter-id', meterData);
        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
    }

    // Bill Group helper methods for template
    isPairedGroup(group: BillGroup): boolean {
        return group.utilityBill !== null && group.supplierBill !== null;
    }

    getBillsFromGroup(group: BillGroup): BillFile[] {
        const bills: BillFile[] = [];
        // Utility bill first, then supplier
        if (group.utilityBill) bills.push(group.utilityBill);
        if (group.supplierBill) bills.push(group.supplierBill);
        return bills;
    }

    formatPeriod(dateStr: string): string {
        if (!dateStr) return 'Undated';
        return dateStr.length >= 7 ? dateStr.substring(0, 7) : dateStr;
    }

    getMatchBadgeClass(confidence: BillGroupMatchConfidence): string {
        // All paired groups use success green
        if (confidence !== 'unpaired') {
            return 'match-badge paired';
        }
        return 'match-badge unpaired';
    }

    getMatchLabel(confidence: BillGroupMatchConfidence): string {
        switch (confidence) {
            case 'high': return 'Paired';
            case 'medium': return 'Likely Pair';
            case 'low': return 'Possible Pair';
            default: return 'Unpaired';
        }
    }

    getConnectorColorClass(confidence: BillGroupMatchConfidence): string {
        switch (confidence) {
            case 'high': return 'connector-high';
            case 'medium': return 'connector-medium';
            case 'low': return 'connector-low';
            default: return 'connector-default';
        }
    }

    getGroupBgClass(confidence: BillGroupMatchConfidence): string {
        // All paired groups use success green
        if (confidence !== 'unpaired') {
            return 'group-bg-paired';
        }
        return 'group-bg-unpaired';
    }

    getGroupAccentClass(confidence: BillGroupMatchConfidence, position: 'first' | 'middle' | 'last'): string {
        // All paired groups use success green accent bar
        if (confidence !== 'unpaired') {
            if (position === 'first') return 'accent-bar paired first';
            if (position === 'last') return 'accent-bar paired last';
            return 'accent-bar paired';
        }
        return 'accent-bar unpaired';
    }

    openPairConfirmation(group: BillGroup) {
        this.openPairReview.emit(group);
    }

    // Promote an identifier to be the primary account number for all bills in this meter
    promoteIdentifierToPrimary(identifier: { key: string; value: string }, event?: Event): void {
        event?.stopPropagation();

        // Update all bills in the meter to use this identifier as the primary account number
        this.meter.bills.forEach((bill: BillFile) => {
            if (bill.data) {
                const updatedData = { ...bill.data };

                // Store current primary in additionalIdentifiers if it exists and is different
                if (updatedData.utilityAccountNumber && updatedData.utilityAccountNumber !== identifier.value) {
                    if (!updatedData.additionalIdentifiers) {
                        updatedData.additionalIdentifiers = [];
                    }
                    // Only store if not already in additionalIdentifiers
                    const alreadyStored = updatedData.additionalIdentifiers.some(id => id.value === updatedData.utilityAccountNumber);
                    if (!alreadyStored) {
                        updatedData.additionalIdentifiers.push({
                            name: 'Previous Account',
                            value: updatedData.utilityAccountNumber,
                            confidence: 1.0
                        });
                    }
                }

                // Set the new primary account number
                updatedData.utilityAccountNumber = identifier.value;

                // Remove from additionalIdentifiers if it was there
                if (updatedData.additionalIdentifiers) {
                    updatedData.additionalIdentifiers = updatedData.additionalIdentifiers.filter(
                        id => id.value !== identifier.value
                    );
                }

                this.billService.updateBillData(bill.id, updatedData);
            }
        });

        // Recalculate identifiers after update
        this.calculateDistinctIds();
    }

    // Handle drag start for identifier
    onIdentifierDragStart(event: DragEvent, identifier: { key: string; value: string }): void {
        event.dataTransfer?.setData('text/plain', identifier.value);
        event.dataTransfer?.setData('identifier-value', identifier.value);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
        event.stopPropagation();
    }

    // Retry processing a failed bill
    onRetryBill(bill: BillFile): void {
        this.billService.processBill(bill.id, bill.file);
    }
}